import { Button, DialogContentText, Popover, Stack } from '@mui/material';
import React, { useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useSnackbar } from '@hooks/useSnackbar';

import { isValidEmail } from '@utils/validation';

import EmailInput from './EmailInput';
import TimeSelector from './TimeSelector';

const messages = defineMessages({
  emailError: {
    id: 'shareButton.createDialog.emailError',
    defaultMessage: 'Please enter at least one email address',
  },
  submit: {
    id: 'shareButton.createDialog.submit',
    defaultMessage: 'Create Share Link',
  },
  instructions: {
    id: 'shareButton.createDialog.instructions',
    defaultMessage:
      'You can choose exactly who has access to what and for how long, giving you total control over your private records. This ensures it remains available to those who need it, and safe from those who don’t.',
  },
  enterEmail: {
    id: 'shareButton.createDialog.enterEmail',
    defaultMessage:
      'Enter the email address of the person you want to share the record with',
  },
  expiry: {
    id: 'shareButton.createDialog.expiry',
    defaultMessage: 'Select a share link expiry option',
  },
  clickButton: {
    id: 'shareButton.createDialog.clickButton',
    defaultMessage: 'Click "Create Share Link"',
  },
  copyLink: {
    id: 'shareButton.createDialog.copyLink',
    defaultMessage:
      'Copy the Share Link and send it to the person who you are sharing it with (you can share the link however you choose to)',
  },
  recipientAccess: {
    id: 'shareButton.createDialog.recipientAccess',
    defaultMessage:
      'The recipient must use the email address you entered below to access the record',
  },
  review: {
    id: 'shareButton.createDialog.review',
    defaultMessage:
      'You can review your Share History and revoke access if needed',
  },
  followSteps: {
    id: 'shareButton.createDialog.followSteps',
    defaultMessage: 'Follow these simple steps:',
  },
  seeSteps: {
    id: 'shareButton.createDialog.seeSteps',
    defaultMessage: 'See Steps',
  },
});

type CreateShareLinkDialogContentProps = {
  onSubmit: (props: { emails: string[]; expires: number }) => void;
};

/**
 * Component for creating a share link dialog content.
 * This component takes a function as a prop, which is called with the selected emails and expiration time when the submit button is clicked.
 */
const CreateShareLinkDialogContent: React.FC<
  CreateShareLinkDialogContentProps
> = ({ onSubmit }) => {
  // Hook for accessing intl methods
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const anchorRef = useRef<HTMLButtonElement | null>(null);

  // State for storing selected emails
  // const [emails, setEmails] = useState<string[]>([]);
  const [{ email1, email2 }, setEmails] = useState({
    email1: '',
    email2: '',
  });

  // State for storing selected expiration time
  const [expires, setExpires] = useState<number>(1);
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);

  const handleClick = () => setPopoverOpen((prev) => !prev);
  const handleClose = () => setPopoverOpen(false);

  /**
   * Function to handle submit button click.
   * Calls the onSubmit function with the selected emails and expiration time.
   */
  const handleSubmit = async () => {
    if (email2.length === 0) {
      return enqueueSnackbar({
        message: formatMessage(messages.emailError),
        variant: 'error',
      });
    }
    onSubmit({ emails: [email2], expires });
  };

  return (
    <Stack spacing={2}>
      <DialogContentText sx={{ color: 'text.primary', fontSize: 15 }}>
        {formatMessage(messages.instructions)}
      </DialogContentText>

      <DialogContentText sx={{ color: 'text.primary', fontSize: 15 }}>
        {formatMessage(messages.followSteps)}
        <Button
          variant="text"
          onClick={handleClick}
          sx={{ textTransform: 'none' }}
        >
          {formatMessage(messages.seeSteps)}
        </Button>
        <Popover
          open={popoverOpen}
          anchorEl={anchorRef.current}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <DialogContentText
            sx={{
              color: 'text.primary',
              fontSize: 15,
              p: 2,
              width: 'fit-content',
            }}
          >
            {formatMessage(messages.followSteps)}

            <ul>
              <li>{formatMessage(messages.enterEmail)}</li>
              <li>{formatMessage(messages.expiry)}</li>
              <li>{formatMessage(messages.clickButton)}</li>
              <li>{formatMessage(messages.copyLink)}</li>
              <li>{formatMessage(messages.recipientAccess)}</li>
              <li>{formatMessage(messages.review)}</li>
            </ul>
          </DialogContentText>
        </Popover>
      </DialogContentText>

      {/* Email selector component for selecting emails */}
      <EmailInput emails={{ email1, email2 }} onChange={setEmails} />
      {/* Time selector component for selecting expiration time */}
      <TimeSelector onChange={setExpires} />
      {/* Button component for submitting the form */}
      <Button
        onClick={handleSubmit}
        disabled={
          !isValidEmail(email1) || !isValidEmail(email2) || email1 !== email2
        }
        variant="contained"
        color="primary"
      >
        {formatMessage(messages.submit)}
      </Button>
    </Stack>
  );
};

export default CreateShareLinkDialogContent;
